import {
  ArrowDown,
  ArrowUp,
  ChevronDown,
  ChevronUp,
  IndianRupee,
  Loader2,
  MapPin,
  Pencil,
  Plus,
  Save,
  Search,
  Trash2,
  Truck,
  X,
} from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useDeliveryChargeConfig } from '../hooks/deliverycharges/useDeliveryChargeConfig';
import { useDeliveryChargeRules } from '../hooks/deliverycharges/useDeliveryChargeRules';
import {
  CreateRulePayload,
  DeliveryChargeRule,
  DeliveryRuleField,
  deliveryChargesService,
} from '../services/deliveryChargesService';
import useToastStore from '../store/useToastStore';

// ── Constants ────────────────────────────────────────────────────────────────

const FIELD_LABELS: Record<DeliveryRuleField, string> = {
  pincode: 'Pincode',
  city: 'City',
  district: 'District',
  state: 'State',
};

const FIELD_COLORS: Record<DeliveryRuleField, string> = {
  pincode: 'bg-violet-100 text-violet-700',
  city: 'bg-blue-100 text-blue-700',
  district: 'bg-cyan-100 text-cyan-700',
  state: 'bg-emerald-100 text-emerald-700',
};

// ── Tag input with async search ───────────────────────────────────────────────

interface TagInputProps {
  field: DeliveryRuleField;
  selected: string[];
  onChange: (values: string[]) => void;
}

const TagInput: React.FC<TagInputProps> = ({ field, selected, onChange }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setQuery('');
    setResults([]);
    setOpen(false);
  }, [field]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (query.trim().length < 1) { setResults([]); setOpen(false); return; }

    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await deliveryChargesService.searchValues(field, query);
        setResults(res.values ?? []);
        setOpen(true);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300);
  }, [query, field]);

  const addValue = (raw: string) => {
    const val = field === 'pincode'
      ? (typeof raw === 'object' ? (raw as any).pincode : raw)
      : raw;
    if (!selected.includes(val)) onChange([...selected, val]);
    setQuery('');
    setResults([]);
    setOpen(false);
    inputRef.current?.focus();
  };

  const removeValue = (val: string) => onChange(selected.filter((v) => v !== val));

  const getLabel = (result: any): string => {
    if (field === 'pincode' && typeof result === 'object') {
      return `${result.pincode} — ${result.city}, ${result.state}`;
    }
    return result as string;
  };

  const getValue = (result: any): string => {
    if (field === 'pincode' && typeof result === 'object') return result.pincode;
    return result as string;
  };

  return (
    <div>
      {/* Selected tags */}
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {selected.map((v) => (
            <span
              key={v}
              className="inline-flex items-center gap-1 text-xs bg-indigo-50 text-indigo-700 border border-indigo-200 px-2 py-0.5 rounded-full font-mono"
            >
              {v}
              <button onClick={() => removeValue(v)} className="text-indigo-400 hover:text-indigo-700">
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Search input */}
      <div className="relative">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => results.length > 0 && setOpen(true)}
            onBlur={() => setTimeout(() => setOpen(false), 150)}
            placeholder={
              field === 'pincode' ? 'Search pincode…'
              : field === 'state' ? 'Search state…'
              : field === 'city' ? 'Search city…'
              : 'Search district…'
            }
            className="w-full pl-8 pr-8 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          />
          {loading && (
            <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 animate-spin" />
          )}
        </div>

        {/* Dropdown */}
        {open && results.length > 0 && (
          <ul className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
            {results.map((r, i) => {
              const val = getValue(r);
              const isSelected = selected.includes(val);
              return (
                <li key={i}>
                  <button
                    type="button"
                    disabled={isSelected}
                    onMouseDown={() => addValue(r)}
                    className={`w-full text-left px-3 py-2 text-sm transition-colors ${
                      isSelected
                        ? 'text-gray-300 cursor-default'
                        : 'text-gray-700 hover:bg-indigo-50 hover:text-indigo-700'
                    }`}
                  >
                    {getLabel(r)}
                    {isSelected && <span className="ml-2 text-xs text-gray-300">already added</span>}
                  </button>
                </li>
              );
            })}
          </ul>
        )}

        {open && !loading && results.length === 0 && query.trim().length > 0 && (
          <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-2 text-sm text-gray-400">
            No results found
          </div>
        )}
      </div>

      {selected.length === 0 && (
        <p className="text-xs text-gray-400 mt-1">Search and select values from the database</p>
      )}
    </div>
  );
};

// ── Rule form modal ───────────────────────────────────────────────────────────

interface RuleFormProps {
  initial?: DeliveryChargeRule;
  nextOrder: number;
  onClose: () => void;
  onSaved: () => void;
}

const RuleForm: React.FC<RuleFormProps> = ({ initial, nextOrder, onClose, onSaved }) => {
  const toast = useToastStore();
  const [name, setName] = useState(initial?.name ?? '');
  const [field, setField] = useState<DeliveryRuleField>(initial?.field ?? 'state');
  const [values, setValues] = useState<string[]>(initial?.values ?? []);
  const [charge, setCharge] = useState(initial?.charge ?? 0);
  const [freeThreshold, setFreeThreshold] = useState(initial?.freeThreshold ?? 0);
  const [saving, setSaving] = useState(false);

  const handleFieldChange = (f: DeliveryRuleField) => {
    setField(f);
    setValues([]);
  };

  const handleSave = async () => {
    if (!name.trim()) return toast.showToast('Rule name is required', 'error');
    if (values.length === 0) return toast.showToast('At least one value is required', 'error');
    if (charge < 0) return toast.showToast('Charge cannot be negative', 'error');

    const payload: CreateRulePayload = {
      name: name.trim(),
      field,
      values,
      charge,
      freeThreshold,
      order: initial?.order ?? nextOrder,
    };

    try {
      setSaving(true);
      if (initial) {
        await deliveryChargesService.updateRule(initial._id, payload);
      } else {
        await deliveryChargesService.createRule(payload);
      }
      toast.showToast(`Rule ${initial ? 'updated' : 'created'} successfully`, 'success');
      onSaved();
      onClose();
    } catch {
      toast.showToast('Failed to save rule', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <h2 className="text-base font-semibold text-gray-900">
            {initial ? 'Edit Rule' : 'Add Rule'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Rule Name</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Rajasthan Zone"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Match By</label>
            <select
              value={field}
              onChange={(e) => handleFieldChange(e.target.value as DeliveryRuleField)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            >
              {(Object.keys(FIELD_LABELS) as DeliveryRuleField[]).map((f) => (
                <option key={f} value={f}>{FIELD_LABELS[f]}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Values</label>
            <TagInput field={field} selected={values} onChange={setValues} />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Charge (₹)</label>
              <input
                type="number"
                min={0}
                value={charge || ''}
                onChange={(e) => setCharge(Number(e.target.value) || 0)}
                placeholder="0"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Free Above (₹) <span className="text-gray-400 font-normal">0 = off</span>
              </label>
              <input
                type="number"
                min={0}
                value={freeThreshold || ''}
                onChange={(e) => setFreeThreshold(Number(e.target.value) || 0)}
                placeholder="0"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
          </div>
        </div>

        <div className="flex gap-3 p-5 border-t border-gray-100">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-medium disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save Rule'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Main page ─────────────────────────────────────────────────────────────────

const DeliveryChargesManagement: React.FC = () => {
  const toast = useToastStore();
  const queryClient = useQueryClient();

  const { data: config, isLoading: configLoading } = useDeliveryChargeConfig();
  const { data: rules = [], isLoading: rulesLoading } = useDeliveryChargeRules();

  const [defaultCharge, setDefaultCharge] = useState(0);
  const [defaultFreeThreshold, setDefaultFreeThreshold] = useState(0);
  const [savingConfig, setSavingConfig] = useState(false);

  const [showForm, setShowForm] = useState(false);
  const [editingRule, setEditingRule] = useState<DeliveryChargeRule | undefined>();
  const [expandedRule, setExpandedRule] = useState<string | null>(null);

  useEffect(() => {
    if (config) {
      setDefaultCharge(config.defaultCharge ?? 0);
      setDefaultFreeThreshold(config.defaultFreeThreshold ?? 0);
    }
  }, [config]);

  const refetch = () => {
    queryClient.invalidateQueries({ queryKey: ['delivery-charge-config'] });
    queryClient.invalidateQueries({ queryKey: ['delivery-charge-rules'] });
  };

  const handleSaveConfig = async () => {
    if (defaultCharge < 0) return toast.showToast('Default charge cannot be negative', 'error');
    try {
      setSavingConfig(true);
      await deliveryChargesService.updateConfig({ defaultCharge, defaultFreeThreshold });
      toast.showToast('Default config saved', 'success');
      refetch();
    } catch {
      toast.showToast('Failed to save config', 'error');
    } finally {
      setSavingConfig(false);
    }
  };

  const handleToggleActive = async (rule: DeliveryChargeRule) => {
    try {
      await deliveryChargesService.updateRule(rule._id, { isActive: !rule.isActive });
      refetch();
    } catch {
      toast.showToast('Failed to update rule', 'error');
    }
  };

  const handleDelete = async (rule: DeliveryChargeRule) => {
    if (!window.confirm(`Delete rule "${rule.name}"?`)) return;
    try {
      await deliveryChargesService.deleteRule(rule._id);
      toast.showToast('Rule deleted', 'success');
      refetch();
    } catch {
      toast.showToast('Failed to delete rule', 'error');
    }
  };

  const handleMove = async (index: number, direction: 'up' | 'down') => {
    const sorted = [...rules].sort((a, b) => a.order - b.order);
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    if (swapIndex < 0 || swapIndex >= sorted.length) return;

    const updates = sorted.map((r, i) => {
      if (i === index) return { id: r._id, order: sorted[swapIndex].order };
      if (i === swapIndex) return { id: r._id, order: sorted[index].order };
      return { id: r._id, order: r.order };
    });

    try {
      await deliveryChargesService.reorderRules(updates);
      refetch();
    } catch {
      toast.showToast('Failed to reorder rules', 'error');
    }
  };

  const isLoading = configLoading || rulesLoading;
  const sortedRules = [...rules].sort((a, b) => a.order - b.order);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-blue-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-blue-50">
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">

        {/* Header */}
        <div>
          <div className="flex items-center gap-3 mb-1">
            <div className="w-8 h-8 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg flex items-center justify-center">
              <Truck className="w-5 h-5 text-white" />
            </div>
            <h1 className="text-2xl font-bold bg-gradient-to-r from-indigo-900 to-blue-600 bg-clip-text text-transparent">
              Delivery Charges
            </h1>
          </div>
          <p className="text-gray-600 text-sm ml-11">
            Rules are evaluated in priority order. First match wins. Unmatched orders use the default config.
          </p>
        </div>

        {/* Default Config */}
        <section className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="bg-gradient-to-r from-slate-50 to-gray-50 px-5 py-4 border-b border-gray-100 flex items-center gap-3">
            <IndianRupee className="w-5 h-5 text-gray-500" />
            <div>
              <h2 className="text-sm font-semibold text-gray-900">Default Config</h2>
              <p className="text-xs text-gray-500">Applied when no rule matches the customer's pincode</p>
            </div>
          </div>
          <div className="p-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Default Charge (₹)</label>
                <input
                  type="number"
                  min={0}
                  value={defaultCharge || ''}
                  onChange={(e) => setDefaultCharge(Number(e.target.value) || 0)}
                  placeholder="0"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Free Delivery Above (₹) <span className="text-gray-400 font-normal">0 = disabled</span>
                </label>
                <input
                  type="number"
                  min={0}
                  value={defaultFreeThreshold || ''}
                  onChange={(e) => setDefaultFreeThreshold(Number(e.target.value) || 0)}
                  placeholder="0"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
            </div>
            <div className="flex justify-end">
              <button
                onClick={handleSaveConfig}
                disabled={savingConfig}
                className="inline-flex items-center gap-2 px-5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg disabled:opacity-50 transition-colors"
              >
                {savingConfig
                  ? <><div className="animate-spin rounded-full h-4 w-4 border border-white border-t-transparent" />Saving…</>
                  : <><Save className="w-4 h-4" />Save Default</>}
              </button>
            </div>
          </div>
        </section>

        {/* Rules */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <MapPin className="w-5 h-5 text-indigo-500" />
              <div>
                <h2 className="text-sm font-semibold text-gray-900">Delivery Rules</h2>
                <p className="text-xs text-gray-500">Evaluated top to bottom — priority #1 is checked first</p>
              </div>
            </div>
            <button
              onClick={() => { setEditingRule(undefined); setShowForm(true); }}
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add Rule
            </button>
          </div>

          {sortedRules.length === 0 ? (
            <div className="bg-white rounded-xl border border-dashed border-gray-300 py-12 text-center">
              <MapPin className="w-8 h-8 text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-gray-500">No rules yet. All orders will use the default config.</p>
              <button
                onClick={() => { setEditingRule(undefined); setShowForm(true); }}
                className="mt-3 text-sm text-indigo-600 hover:text-indigo-700 font-medium"
              >
                Add your first rule →
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {sortedRules.map((rule, index) => {
                const isExpanded = expandedRule === rule._id;
                const showMore = rule.values.length > 3;
                const displayValues = isExpanded ? rule.values : rule.values.slice(0, 3);

                return (
                  <div
                    key={rule._id}
                    className={`bg-white rounded-xl border shadow-sm transition-all ${
                      rule.isActive ? 'border-gray-200' : 'border-gray-100 opacity-60'
                    }`}
                  >
                    <div className="flex items-start gap-3 p-4">
                      <div className="flex flex-col items-center gap-1 pt-0.5">
                        <button
                          onClick={() => handleMove(index, 'up')}
                          disabled={index === 0}
                          className="text-gray-300 hover:text-gray-600 disabled:opacity-20"
                        >
                          <ArrowUp className="w-3.5 h-3.5" />
                        </button>
                        <span className="text-xs font-bold text-indigo-600 w-5 text-center">{index + 1}</span>
                        <button
                          onClick={() => handleMove(index, 'down')}
                          disabled={index === sortedRules.length - 1}
                          className="text-gray-300 hover:text-gray-600 disabled:opacity-20"
                        >
                          <ArrowDown className="w-3.5 h-3.5" />
                        </button>
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-sm font-semibold text-gray-900 truncate">{rule.name}</span>
                          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${FIELD_COLORS[rule.field]}`}>
                            {FIELD_LABELS[rule.field]}
                          </span>
                          {!rule.isActive && (
                            <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">Inactive</span>
                          )}
                        </div>

                        <div className="flex flex-wrap gap-1 mb-2">
                          {displayValues.map((v) => (
                            <span key={v} className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded font-mono">{v}</span>
                          ))}
                          {showMore && (
                            <button
                              onClick={() => setExpandedRule(isExpanded ? null : rule._id)}
                              className="text-xs text-indigo-500 hover:text-indigo-700 flex items-center gap-0.5"
                            >
                              {isExpanded
                                ? <><ChevronUp className="w-3 h-3" />less</>
                                : <><ChevronDown className="w-3 h-3" />+{rule.values.length - 3} more</>}
                            </button>
                          )}
                        </div>

                        <div className="flex gap-4 text-xs text-gray-500">
                          <span>Charge: <span className="font-semibold text-gray-800">₹{rule.charge}</span></span>
                          <span>Free above: <span className="font-semibold text-gray-800">{rule.freeThreshold > 0 ? `₹${rule.freeThreshold}` : 'off'}</span></span>
                        </div>
                      </div>

                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button
                          onClick={() => handleToggleActive(rule)}
                          title={rule.isActive ? 'Disable' : 'Enable'}
                          className={`w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold transition-colors ${
                            rule.isActive
                              ? 'bg-emerald-50 text-emerald-600 hover:bg-emerald-100'
                              : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                          }`}
                        >
                          {rule.isActive ? '●' : '○'}
                        </button>
                        <button
                          onClick={() => { setEditingRule(rule); setShowForm(true); }}
                          className="w-7 h-7 rounded-lg bg-indigo-50 text-indigo-600 hover:bg-indigo-100 flex items-center justify-center"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => handleDelete(rule)}
                          className="w-7 h-7 rounded-lg bg-red-50 text-red-500 hover:bg-red-100 flex items-center justify-center"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </main>

      {showForm && (
        <RuleForm
          initial={editingRule}
          nextOrder={sortedRules.length + 1}
          onClose={() => { setShowForm(false); setEditingRule(undefined); }}
          onSaved={refetch}
        />
      )}
    </div>
  );
};

export default DeliveryChargesManagement;
